import React, { useCallback, useEffect, useState } from "react";

import { useBankData, useCardData } from "store/slices/paymentMethodSlice";
import { usePaymentPlanData } from "store/slices/dashboardSlice";
import SavedPaymentMethodList from "components/paymentMethod/SavedPaymentMethodList";
import { useUserData } from "store/slices/authSlice";
import type {
  PaymentProcessStep2Props,
  PaymentSelectionState,
  FormattedPaymentMethodTypes,
} from "types/paymentMethodComponent";
import { getCountryShortName } from "utils/HelperFunctions";
import { getCountryCallingCode } from "react-phone-number-input";
import { CountryCode } from "types/UserProfile";
import MobileInputNew from "components/form/MobileInputNew";
import { setIsPaymentProcessing } from "store/slices/paymentsSlice";
import { useAppDispatch } from "store/hooks";
import { useTestAttrsFactory } from "utils/testid";

const PaymentProcessStep2 = ({
  data,
  setData,
  isDataLoading,
  currentStep,
  walletList,
  setSavedCardsEmail = () => {},
  isPaymentLink = true,
  setShowPreview = () => {},
  customClassName = "",
  paymentMethodList,
  setSelectedMethod = () => {},
  collectPhone = false,
  phoneNumber = { phone: "", postal_code: "", country_code: "" },
  setPhoneNumber = () => {},
  custom_conv = "on",
  handleEmailBlur = () => {},
  cppId = null,
  amount,
  onPaymentMethodChange = () => {},
}: PaymentProcessStep2Props): JSX.Element => {
  const testAttrs = useTestAttrsFactory();
  const paymentPlanDataRedux = usePaymentPlanData();
  const userData = useUserData();
  const dispatch = useAppDispatch();
  let accountData = userData?.data?.accountData;
  const [paymentSelectionState, setPaymentSelectionState] =
    useState<PaymentSelectionState>({
      showAllCards: 3,
      showAllBanks: 3,
      showAllWallets: 2,
      email: "",
    });
  const cardList = useCardData();
  const bankList = useBankData();
  useEffect(() => {
    if (paymentPlanDataRedux && currentStep === 2 && cardList && bankList) {
      const tmpForCard = { ...data };
      if (paymentPlanDataRedux?.clientCustomerData?.payment_method === "CC") {
        delete tmpForCard.selectedBank;
        const findSelectedCard = cardList?.find(
          (itm: FormattedPaymentMethodTypes) =>
            itm.id === paymentPlanDataRedux.clientCustomerData.cppid ||
            itm.cc_token ===
              paymentPlanDataRedux.clientCustomerData.cc_ref_number
        );
        setData({ ...tmpForCard, selectedCard: findSelectedCard });
      }
      if (paymentPlanDataRedux?.clientCustomerData?.payment_method === "ACH") {
        delete tmpForCard.selectedCard;
        const findSelectedBank = bankList?.find(
          (itm: FormattedPaymentMethodTypes) =>
            itm.cc_token ===
            paymentPlanDataRedux.clientCustomerData.cc_ref_number
        );
        setData({ ...tmpForCard, selectedBank: findSelectedBank });
      }
    }
  }, [paymentPlanDataRedux, cardList, bankList, currentStep, setData]);

  const handlePhoneCountryChange = useCallback((itm: string) => {
    setPhoneNumber((state) => ({
      ...state,
      country_code: getCountryCallingCode(itm as CountryCode),
    }));
  }, []);

  const handleOnPhoneChange = useCallback((phone: string) => {
    setPhoneNumber((state) => ({
      ...state,
      phone,
    }));
  }, []);

  const handleCardOrBankSelect = (
    type: string,
    id: number,
    isDefault: boolean = false
  ): void => {
    const tmpData = { ...data };
    if (!isDefault) {
      dispatch(setIsPaymentProcessing(true));
    }
    switch (type) {
      case "Card":
        delete tmpData.selectedBank;
        delete tmpData.selectedWallet;
        const findSelectedCard = cardList?.find(
          (itm: FormattedPaymentMethodTypes) => itm.id === id
        );
        setData({ ...tmpData, selectedCard: findSelectedCard });
        setSelectedMethod("Card");
        if (!isDefault) {
          onPaymentMethodChange({
            method: "Card",
            paymentMethodId:
              findSelectedCard?.formatted?.id ?? findSelectedCard?.id,
          });
        }
        break;
      case "Bank":
        delete tmpData.selectedCard;
        delete tmpData.selectedWallet;
        const findSelectedBank = bankList?.find(
          (itm: FormattedPaymentMethodTypes) => itm.id === id
        );
        setData({ ...tmpData, selectedBank: findSelectedBank });
        setSelectedMethod("Bank");
        if (!isDefault) {
          onPaymentMethodChange({
            method: "Bank",
            paymentMethodId:
              findSelectedBank?.formatted?.id ?? findSelectedBank?.id,
          });
        }
        break;
      case "Wallets":
        delete tmpData.selectedCard;
        delete tmpData.selectedBank;
        const findSelectedWallets = walletList?.find(
          (itm: FormattedPaymentMethodTypes) => itm.id === id
        );
        setData({ ...tmpData, selectedWallet: findSelectedWallets });
        const walletMethodMap: Record<string, string> = {
          CAPP: "Cash App Pay",
          AMZP: "Amazon Pay",
          PP: "PayPal",
          GP: "Google Pay",
          AP: "Apple Pay",
        };
        const method =
          walletMethodMap[findSelectedWallets?.payment_method || ""] ||
          "Wallets";
        setSelectedMethod(method);
        if (!isDefault) {
          onPaymentMethodChange({
            method,
            paymentMethodId:
              findSelectedWallets?.formatted?.id ?? findSelectedWallets?.id,
          });
        }
        break;
    }
  };

  useEffect(() => {
    if (accountData?.email) {
      setSavedCardsEmail(accountData?.email);
    }
  }, [accountData]);

  return (
    <>
      {/* HEADER PART */}

      <div
        className={`mb-2 ${customClassName}`}
        {...testAttrs("saved-payment-step", "Saved payment method step")}
      >
        {/* CONTENT */}
        <div {...testAttrs("saved-payment-methods", "Saved payment methods")}>
          <SavedPaymentMethodList
            cardList={cardList}
            bankList={bankList}
            walletList={walletList}
            isDataLoading={isDataLoading}
            showAllCards={paymentSelectionState.showAllCards}
            handleCardOrBankSelect={handleCardOrBankSelect}
            custom_conv={custom_conv}
            setShowAllCards={(value: number | ((prev: number) => number)) => {
              setPaymentSelectionState((prev) => ({
                ...prev,
                showAllCards:
                  typeof value === "function"
                    ? value(prev.showAllCards)
                    : value,
              }));
            }}
            showAllBanks={paymentSelectionState.showAllBanks}
            setShowAllBanks={(value: number | ((prev: number) => number)) => {
              setPaymentSelectionState((prev) => ({
                ...prev,
                showAllBanks:
                  typeof value === "function"
                    ? value(prev.showAllBanks)
                    : value,
              }));
            }}
            showAllWallets={paymentSelectionState.showAllWallets}
            setShowAllWallets={(value: number | ((prev: number) => number)) => {
              setPaymentSelectionState((prev) => ({
                ...prev,
                showAllWallets:
                  typeof value === "function"
                    ? value(prev.showAllWallets)
                    : value,
              }));
            }}
            data={data}
            isPaymentLink={isPaymentLink}
            setShowPreview={setShowPreview}
            paymentMethodList={paymentMethodList}
            cppId={cppId}
            amount={amount}
          />
        </div>
        <div
          className="mt-4"
          {...testAttrs("saved-payment-contact", "Saved payment contact")}
        >
          <div className="text-sm leading-[18px] text-txtSecondary mb-2">
            EMAIL
          </div>
          <div className="relative w-full">
            <input
              autoComplete="off"
              {...testAttrs("saved-payment-email", "Payment receipt email")}
              className={`w-full border-strokePrimary rounded-[5px] border py-2 px-[15px] bg-bgPrimary text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
                accountData?.email
                  ? "!cursor-not-allowed	!bg-bgSecondary	"
                  : "cursor-pointer"
              }`}
              placeholder="Email"
              onChange={(e) => {
                const emailValue = accountData?.email
                  ? accountData?.email
                  : e.target.value;
                setPaymentSelectionState((prev) => ({
                  ...prev,
                  email: emailValue,
                }));
                setSavedCardsEmail(emailValue);
              }}
              onBlur={() => {
                handleEmailBlur();
              }}
              value={
                accountData?.email
                  ? accountData?.email
                  : paymentSelectionState.email
              }
              disabled={!!accountData?.email}
            />
          </div>

          {collectPhone && (
            <div className="pt-5">
              <div className={`!text-sm leading-[18px] text-txtSecondary mb-2`}>
                Phone
              </div>
              <MobileInputNew
                inputValue={phoneNumber?.phone || ""}
                isProductLinkInput={true}
                aria-label="Payment phone number"
                data-testid={testAttrs("saved-payment-phone")["data-testid"]}
                userCountry={
                  getCountryShortName(phoneNumber?.country_code) || "US"
                }
                setCountry={handlePhoneCountryChange}
                setInputValue={handleOnPhoneChange}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentProcessStep2;
